import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { ZitaLogo, ZitaWordmark } from '@/components/ZitaLogo';
import { PublicFooter } from '@/components/PublicFooter';
import { PasswordInput } from '@/components/PasswordInput';
import { AUTH } from '@/constants/testIds';
import { toast } from 'sonner';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email.trim().toLowerCase(), password, remember);
      toast.success('Welcome back.');
      // Doctrine v2.0: trial-expired / payment-failed users go to /subscribe,
      // not the marketing /billing page. Active users go straight to the app.
      navigate(user.has_active_access ? '/' : '/subscribe');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <ZitaLogo size={44} />
          <ZitaWordmark className="text-3xl" />
        </Link>
        <form onSubmit={submit} className="zita-card p-8 w-full max-w-md space-y-4">
          <h1 className="font-serif text-3xl text-zita-espresso">Sign in</h1>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1" htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={AUTH.loginEmail}
              autoComplete="email"
              className="zita-input w-full px-4 py-3 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1" htmlFor="login-password">
              Password
            </label>
            <PasswordInput
              id="login-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              testId={AUTH.loginPassword}
              autoComplete="current-password"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-zita-mocha">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              data-testid={AUTH.loginRemember}
            />
            Remember me
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid={AUTH.loginSubmit}
            className="zita-btn-primary w-full"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <div className="text-sm text-zita-mocha flex justify-between">
            <Link to="/forgot" className="hover:text-zita-cocoa">Forgot password?</Link>
            <Link to="/register" className="hover:text-zita-cocoa">Create account</Link>
          </div>
        </form>
      </main>
      <PublicFooter />
    </div>
  );
};

export default Login;
